import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import createNotification, {
  ERROR_NOTIFICATION,
  SUCCESS_NOTIFICATION,
} from "../../../components/Notifications";
import {
  cipAndShutdownConfig,
  DEFAULT_PRESSURE_DROP_ABS_THRESHOLD_BAR,
} from "./AdvancedSettings.config";

import { mapSettingsToRedux } from "../../../utils/Utils";

import {
  getadvanceSettings,
  setadvanceSettings,
} from "../../../network/cipAPI";

import {
  storeAdvancedSettings,
  storeSettingsDefaults,
} from "../../../Store/TemplateStore/template.action";

const toBool = (value) => value === "1" || value === true;

const useAdvancedSettings = ({ selectedTrain }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const pressure = useSelector((state) => state.pressure);
  const advancedSettingsFromRedux = useSelector(
    (state) => state.advancedSettings,
  );
  const cipDynamicConfig = cipAndShutdownConfig(pressure);

  // Derive the unit-dependent absolute pressure drop default, then merge
  // with the Redux defaults — no need to read every Redux field individually.
  const pressure_drop_abs_threshold =
    pressure === "psig"
      ? 15 // psig default matches Redux initial pressureDropThresholdAbsolute
      : DEFAULT_PRESSURE_DROP_ABS_THRESHOLD_BAR;

  const defaultSettings = {
    ...advancedSettingsFromRedux,
    pressure_drop_abs_threshold,
  };

  const fetchSettings = async (setShowOverlayLoader) => {
    setShowOverlayLoader(true);
    try {
      const response = await getadvanceSettings(selectedTrain);
      if (response?.detail) {
        createNotification(
          ERROR_NOTIFICATION,
          "Something went wrong, please try again later.",
          "",
        );
        return null;
      }
      if (!response?.data) return null;

      const {
        settings: rawSettings = {},
        suggested_settings: rawSuggestedSettings = {},
      } = response.data;

      // Guard against null payloads — destructuring defaults only apply for undefined, not null
      const settingsData =
        rawSettings && typeof rawSettings === "object" ? rawSettings : {};
      const suggestedData =
        rawSuggestedSettings && typeof rawSuggestedSettings === "object"
          ? rawSuggestedSettings
          : {};
      const {
        pressure_drop_threshold_flag = 0,
        pressure_drop_threshold_flag_predict = 0,
        use_permeability_trigger,
      } = settingsData;

      // Flags may be returned as strings ("0", "1", "2") from the API — coerce to Number
      // so that strict equality checks below work correctly.
      const pdFlag = Number(pressure_drop_threshold_flag ?? 0);
      const pdFlagPredict = Number(pressure_drop_threshold_flag_predict ?? 0);
      // Fields passed through as-is (only when present in the API response)
      const scalarFields = [
        "n_neighbors",
        "n",
        "window",
        "pf_threshold",
        "pressure_drop_threshold",
        "pressure_drop_abs_threshold",
        "salt_passage_threshold",
        "min_day_between_cips",
        "max_days_between_cips",
        "k",
        "shutdown_length",
        "permeability_threshold",
        "permeability_threshold_predict",
        "min_time",
        "max_time",
        "min_num_points",
        "stabilization_days",
        "top_exp",
        "max_evals",
        "optimization_target",
        "pressure_drop_threshold_predict",
        "pressure_drop_abs_threshold_predict",
        "failure_probability_threshold",
      ];

      // Fields that need "1"/true → boolean coercion
      const boolFields = [
        "use_salt_passage",
        "permeability_only",
        "adapt_pf_trend",
        "adapt_pressure_drop_trend",
        "adapt_salt_passage_trend",
        "simulation_improve_accuracy",
      ];

      const transformed = {
        ...defaultSettings,
        ...Object.fromEntries(
          scalarFields
            .filter((k) => settingsData[k] != null)
            .map((k) => [k, settingsData[k]]),
        ),
        ...Object.fromEntries(
          boolFields
            .filter((k) => settingsData[k] != null)
            .map((k) => [k, toBool(settingsData[k])]),
        ),
        // Explicit default: true (not false) when the API omits this field
        use_permeability_trigger: toBool(use_permeability_trigger ?? true),
        pressure_drop_threshold_predictCheckbox1:
          pdFlagPredict === 0 || pdFlagPredict === 2,
        pressure_drop_threshold_predictCheckbox2:
          pdFlagPredict === 1 || pdFlagPredict === 2,
      };

      const checkedFromFlag = {
        percentage: pdFlag === 0 || pdFlag === 2,
        absolute: pdFlag === 1 || pdFlag === 2,
      };

      dispatch(storeSettingsDefaults(mapSettingsToRedux(transformed)));
      dispatch(storeAdvancedSettings(transformed));
      return { transformed, checkedFromFlag, suggestedData };
    } catch (error) {
      createNotification(
        ERROR_NOTIFICATION,
        error.message || t("ValueStrings.somethingWentWrong"),
        "",
      );
      return null;
    } finally {
      setShowOverlayLoader(false);
    }
  };

  const preparePayload = (settings, checkedPressureDropOptions) => {
    const outlierPayload = {
      n_neighbors: settings.n_neighbors,
      window: settings.window,
      n: settings.n,
    };

    const percentChecked = checkedPressureDropOptions.percentage;
    const absoluteChecked = checkedPressureDropOptions.absolute;
    // Always send both values so the API can persist the flag correctly;
    // the flag itself tells the backend which threshold(s) are active.
    const pressure_drop_threshold_flag =
      percentChecked && absoluteChecked
        ? 2
        : absoluteChecked
          ? 1
          : percentChecked
            ? 0
            : undefined;

    const cipShutdownPayload = {
      pressure_drop_threshold: settings.pressure_drop_threshold,
      pressure_drop_abs_threshold: settings.pressure_drop_abs_threshold,
      pressure_drop_threshold_flag,
      salt_passage_threshold: settings.salt_passage_threshold,
      min_day_between_cips: settings.min_day_between_cips,
      max_days_between_cips: settings.max_days_between_cips,
      k: settings.k,
      shutdown_length: settings.shutdown_length,
      use_salt_passage: settings.use_salt_passage,
      use_permeability_trigger: settings.use_permeability_trigger,
      permeability_only: settings.permeability_only,
      permeability_threshold: settings.permeability_threshold,
    };

    const cleaningEffectivenessPayload = {
      min_time: settings.min_time,
      max_time: settings.max_time,
      min_num_points: settings.min_num_points,
      stabilization_days: settings.stabilization_days,
    };

    // pf_threshold is a physics parameter shared by both CIP shutdown and
    // prediction sections; it lives here since it's consumed by the prediction model.
    const predictCb1 = settings.pressure_drop_threshold_predictCheckbox1;
    const predictCb2 = settings.pressure_drop_threshold_predictCheckbox2;
    // Fall back to defaults if the user hasn't changed them yet
    const predictPercentChecked =
      predictCb1 === undefined ? true : !!predictCb1;
    const predictAbsoluteChecked =
      predictCb2 === undefined ? false : !!predictCb2;
    const pressure_drop_threshold_flag_predict =
      predictPercentChecked && predictAbsoluteChecked
        ? 2
        : predictAbsoluteChecked
          ? 1
          : predictPercentChecked
            ? 0
            : undefined;

    const cipPredictionPayload = {
      pf_threshold: settings.pf_threshold,
      top_exp: settings.top_exp,
      max_evals: settings.max_evals,
      permeability_threshold_predict: settings.permeability_threshold_predict,
      adapt_pf_trend: settings.adapt_pf_trend,
      adapt_pressure_drop_trend: settings.adapt_pressure_drop_trend,
      adapt_salt_passage_trend: settings.adapt_salt_passage_trend,
      simulation_improve_accuracy: settings.simulation_improve_accuracy,
      optimization_target: settings.optimization_target,
      pressure_drop_threshold_flag_predict,
      ...(predictPercentChecked && {
        pressure_drop_threshold_predict:
          settings.pressure_drop_threshold_predict,
      }),
      ...(predictAbsoluteChecked && {
        pressure_drop_abs_threshold_predict:
          settings.pressure_drop_abs_threshold_predict,
      }),
    };

    const lifetimePayload = {
      failure_probability_threshold: settings.failure_probability_threshold,
    };

    return {
      ...outlierPayload,
      ...cipShutdownPayload,
      ...cleaningEffectivenessPayload,
      ...cipPredictionPayload,
      ...lifetimePayload,
    };
  };

  const saveSettings = async (
    settings,
    checkedPressureDropOptions,
    setShowOverlayLoader,
  ) => {
    setShowOverlayLoader(true);
    try {
      const payload = preparePayload(settings, checkedPressureDropOptions);
      const response = await setadvanceSettings(payload, selectedTrain);
      if (response?.detail) {
        createNotification(
          ERROR_NOTIFICATION,
          "Something went wrong, please try again later.",
          "",
        );
        return false;
      }
      if (response) {
        dispatch(storeSettingsDefaults(mapSettingsToRedux(response)));
        dispatch(storeAdvancedSettings(response));
        createNotification(
          SUCCESS_NOTIFICATION,
          t("advanceSettings.savemsg"),
          "",
        );
        return true;
      }
      return false;
    } catch (error) {
      createNotification(
        ERROR_NOTIFICATION,
        error.message || t("ValueStrings.somethingWentWrong"),
        "",
      );
      return false;
    } finally {
      setShowOverlayLoader(false);
    }
  };

  const buildDefaultChecked = (cipDynamicConfig) => {
    const { defaultSelected, options } =
      cipDynamicConfig.pressure_drop_threshold;
    return Object.fromEntries(
      Object.keys(options).map((k) => [k, k === defaultSelected]),
    );
  };
  return {
    pressure,
    cipDynamicConfig,
    defaultSettings,
    fetchSettings,
    saveSettings,
    buildDefaultChecked,
  };
};

export default useAdvancedSettings;
